import { SSO_LANDING_MARKER, SSO_LANDING_PARAM } from '@commutifi/constants';
import { IdentificationProtocol } from '@commutifi/constants/Authentication';
import { Account } from '@commutifi-fe/interfaces';
import { validateAndExtractClientState } from 'shared/hooks/oauth/utils';
import { stringify } from 'utils/queryParams';
import config from 'config/index';
import { apiCall, dashboardApiServer, JSON_HEADER } from '../../index';
import endpoints from './endpoints';
export { IdentificationProtocol };

// Uses api-platform for request
const apiDashboardServer = apiCall(dashboardApiServer);
export interface LoginBody {
  email: string;
  password: string;
}
export interface LoginResponse {
  redirectPath?: string;
  account?: Account;
}
export type PasswordResetBody = Pick<LoginBody, 'password'> & {
  passwordResetToken: string;
};
export const loginUser = (auth: LoginBody): Promise<LoginResponse> => apiDashboardServer(endpoints.POST.Authentication.route(), {
  method: 'post',
  headers: JSON_HEADER,
  data: JSON.stringify(auth)
});
export const logoutUser = () => apiDashboardServer(endpoints.POST.Logout.route(), {
  method: 'post',
  headers: JSON_HEADER
});
export const refreshAccessToken = () => apiDashboardServer(endpoints.PUT.RefreshToken.route(), {
  method: 'put',
  headers: JSON_HEADER
});
export const requestPasswordReset = (user: Pick<LoginBody, 'email'>) => apiDashboardServer(endpoints.POST.ForgotPassword.route(), {
  method: 'post',
  headers: JSON_HEADER,
  data: JSON.stringify(user)
});
export const resetPassword = (auth: PasswordResetBody) => apiDashboardServer(endpoints.POST.ResetPassword.route(), {
  method: 'post',
  headers: JSON_HEADER,
  data: JSON.stringify(auth)
});
export const fetchIdentityProviderUrl = (identityProviderId: string, queryParams: Record<string, any>) => apiDashboardServer(endpoints.GET.IDPUrl.route(identityProviderId), {
  method: 'get',
  queryParams
});
export interface SSOLoginState {
  targetUrl: string;
  identityProviderId: string;
  shortlinkId?: string;
  subdomain: string;
}
export function signWithIdentityProvider<Res = any>(oauthParams: {
  authorizationCode: string;
  state: string;
  nonce: string;
  identityProviderId: string;
}): Promise<Res> {
  const state = validateAndExtractClientState<SSOLoginState>();
  return apiDashboardServer(endpoints.POST.IDPAuth.route(state?.identityProviderId || ''), {
    method: 'post',
    data: oauthParams,
    headers: {
      ...(state?.shortlinkId && {
        'x-commutifi-shortlinkid': state.shortlinkId
      })
    }
  });
}
export const accountIdentificationProtocols = (email: string): Promise<IdentificationProtocolsResponse> => apiDashboardServer(endpoints.POST.IdentificationProtocols.route(), {
  method: 'post',
  data: {
    email
  }
});
export const requestOTP = (email: string): Promise<void> => apiDashboardServer(endpoints.POST.OTP.route(), {
  method: 'post',
  data: {
    email
  }
});
export const validateOTP = (email: string, otp: string): Promise<{
  account: Account;
}> => apiDashboardServer(endpoints.POST.OTPValidation.route(), {
  method: 'post',
  data: {
    email,
    otp
  }
});
export const resendOTP = (email: string): Promise<void> => apiDashboardServer(endpoints.POST.ResendOTP.route(), {
  method: 'post',
  data: {
    email
  }
});

// The login screen holds its form behind this probe, so it has to fail fast. The shared client
// retries 5xx three times with exponential backoff (~14s all told); letting that run would keep
// password login unreachable while an *optional* capability check is degraded.
const SSO_INITIATE_TIMEOUT_MS = 3000;

/**
 * Pre-auth gate the login screen calls to decide whether to offer the "Sign in with SSO" button for
 * this subdomain. The dashboard returns only `{ ssoEnabled }`; the WorkOS org id stays server-side,
 * since the FE never needs it — `/sso/login` re-resolves it.
 *
 * @param subdomain - Tenant subdomain, from the URL rather than any API response
 */
export const fetchSsoInitiate = (subdomain: string): Promise<SsoInitiateResponse> => apiDashboardServer(endpoints.GET.WorkOsSsoInitiate.route(), {
  method: 'get',
  params: {
    subdomain
  },
  timeout: SSO_INITIATE_TIMEOUT_MS,
  'axios-retry': {
    retries: 0
  }
});

/**
 * Absolute URL for the dashboard route that starts org-scoped WorkOS SSO.
 *
 * The flow is fully server-side: the browser navigates full-page to this route, which resolves the
 * enterprise's WorkOS org from `subdomain`, redirects to WorkOS, handles the callback and code
 * exchange, establishes the session cookie, and 302s back to the app. The auth code never touches
 * browser JS, so the FE only needs somewhere to navigate — no XHR, no client-side exchange.
 *
 * Serialized through the shared `stringify` like every other module here, rather than
 * `URLSearchParams`. The two disagree on spaces — `+` vs `%20` — and `returnTo` is a path the
 * dashboard reflects back into a redirect, so whether `+` survives as a space depends on which
 * parser reads it.
 *
 * @param subdomain - Required; the dashboard treats a missing one as a caller error
 * @param options.returnTo - Where the app resumes after login, as a path relative to the SPA origin
 */
export const buildSsoLoginUrl = (subdomain: string, options: {
  returnTo?: string;
} = {}): string => {
  const base = `${config.dashboardApiUrl}${endpoints.GET.WorkOsSsoLogin.route()}`;
  return `${base}?${stringify({
    subdomain,
    returnTo: options.returnTo || undefined
  })}`;
};

// Contract with the dashboard's WorkOS SSO callback: after it establishes the session cookie it
// 302s back to the SPA with `?sso=1` appended. Because that redirect is not an XHR, nothing writes
// the SPA's `account:id` login gate, so useSsoLandingHydration keys off this marker to hydrate the
// session once on landing. Non-secret hint only — no accountId or token ever rides in the URL.
// One object rather than two constants, so a caller cannot import half of the contract; the
// literals live in @commutifi/constants (shared with the dashboard) so neither side drifts alone.
export const SSO_LANDING = {
  param: SSO_LANDING_PARAM,
  marker: SSO_LANDING_MARKER
} as const;

// Types
export type IdentificationProtocolsResponse = {
  availableProtocols: IdentificationProtocol[];
  allowIdentification: boolean;
};
export type SsoInitiateResponse = {
  ssoEnabled: boolean;
};